// src/pages/business/BusinessSettings.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Building2,
  Clock,
  Settings,
  Mail,
  Save,
  CheckCircle,
  ArrowLeft,
  Copy,
  Check,
  AlertCircle,
  CreditCard,
  Zap,
  Info,
  Plus,
  ChevronDown,
  ChevronUp,
  Globe,
  Gift
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Import shadcn components
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';

// Import custom components
import PageContainer from '../../components/ui/layout/PageContainer/PageContainer';
import GBPSettings from '../../components/configuration/sections/GBPSettings';
import MicrosoftIntegrationSettings from '../../components/configuration/sections/MicrosoftIntegrationSettings';
import ZoomIntegrationSettings from '../../components/configuration/sections/ZoomIntegrationSettings';
import MeetingProviderPreference from '../../components/configuration/sections/MeetingProviderPreference';
import CalendarIntegrationSettings from '../../components/configuration/sections/CalendarIntegrationSettings';
import TeamsIntegrationSettings from '../../components/configuration/sections/TeamsIntegrationSettings';
import ReferralSettings from '../../components/configuration/sections/ReferralSettings';

// Import hooks
import { useSettings } from '../../hooks/useSettings';
import { useStripe } from '../../hooks/useStripe';
import { cn } from '../../utils/cn';

const BusinessSettings = () => {
  const navigate = useNavigate();

  // Get settings data
  const {
    businessSettings,
    businessHours,
    emailSettings,
    emailDomains,
    loading,
    savingCompanyInfo,
    savingOperationalSettings,
    savingHours,
    savingEmailSettings,
    checkingDomainVerification,
    updateSettings,
    updateBusinessHours,
    updateEmailSettings,
    setupProfessionalDomain,
    setActiveEmailDomain,
    setBasicEmailMode,
    checkDomainVerification,
    verifyDomain,
    getEmailDomains,
    getVerifiedEmailDomains,
    getActiveEmailDomain
  } = useSettings();

  // Get Stripe data
  const {
    stripeConnection,
    status,
    statusMessage,
    nextAction,
    canAcceptPayments,
    stripeLoading,
    setupInProgress,
    refreshingStatus,
    disconnecting,
    loadStripeConnectionStatus,
    startExpressSetup,
    createOnboardingLink,
    checkOnboardingStatus,
    getExpressDashboardLink,
    disconnectStripeAccount,
    onboardingUrl,
    dashboardUrl
  } = useStripe();

  // Navigation state
  const [activeSection, setActiveSection] = useState('company');
  const [ready, setReady] = useState(false);

  // Section refs for scroll-to functionality
  const sectionRefs = {
    company: useRef(null),
    hours: useRef(null),
    operational: useRef(null),
    email: useRef(null),
    payment: useRef(null),
    referrals: useRef(null),
    integrations: useRef(null)
  };

  // Store calculated section positions
  const sectionPositions = useRef({});

  // ===== COMPANY INFORMATION SECTION =====
  const initialCompanyForm = {
    company_name: '',
    company_phone: '',
    company_address: '',
    company_city: '',
    company_state: 'TX',
    company_zip: '',
    company_email: '',
    company_website: ''
  };
  const [companyForm, setCompanyForm] = useState(initialCompanyForm);
  const [originalCompanyForm, setOriginalCompanyForm] = useState(initialCompanyForm);

  // Check for company form changes
  const hasCompanyChanges = Object.keys(companyForm).some(key =>
    companyForm[key] !== (originalCompanyForm[key] || '')
  );

  // Initialize company form from settings (only when no pending edits)
  useEffect(() => {
    if (businessSettings && Object.keys(businessSettings).length > 0) {
      const formData = {
        company_name: businessSettings.company_name || '',
        company_phone: businessSettings.company_phone || '',
        company_address: businessSettings.company_address || '',
        company_city: businessSettings.company_city || '',
        company_state: businessSettings.company_state || 'TX',
        company_zip: businessSettings.company_zip || '',
        company_email: businessSettings.company_email || '',
        company_website: businessSettings.company_website || ''
      };
      if (!hasCompanyChanges) {
        setCompanyForm(formData);
      }
      setOriginalCompanyForm(formData);
    }
  }, [businessSettings]);

  // Watch saving flag transition: re-snapshot baseline on save completion
  const prevSavingCompany = useRef(false);
  useEffect(() => {
    if (prevSavingCompany.current && !savingCompanyInfo) {
      // Save just completed (true -> false). Re-snapshot from store truth.
      const newBaseline = {
        company_name: businessSettings.company_name || '',
        company_phone: businessSettings.company_phone || '',
        company_address: businessSettings.company_address || '',
        company_city: businessSettings.company_city || '',
        company_state: businessSettings.company_state || 'TX',
        company_zip: businessSettings.company_zip || '',
        company_email: businessSettings.company_email || '',
        company_website: businessSettings.company_website || ''
      };
      setOriginalCompanyForm(newBaseline);
      setCompanyForm(newBaseline);
    }
    prevSavingCompany.current = savingCompanyInfo;
  }, [savingCompanyInfo, businessSettings]);

  // Handle company form submit - diff-only, fire and forget
  const handleCompanySubmit = () => {
    const changedFields = {};
    Object.keys(companyForm).forEach(key => {
      if (companyForm[key] !== originalCompanyForm[key]) {
        changedFields[key] = companyForm[key];
      }
    });
    if (Object.keys(changedFields).length > 0) {
      updateSettings(changedFields, { savingFlag: 'savingCompanyInfo' });
    }
  };

  // ===== BUSINESS HOURS SECTION =====
  const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  const [hoursForm, setHoursForm] = useState({});
  const [originalHoursForm, setOriginalHoursForm] = useState({});

  // Conversion functions
  const convertNumberKeysToNames = (hoursData) => {
    if (!hoursData) return {};
    const converted = {};
    Object.keys(hoursData).forEach(key => {
      const dayNumber = parseInt(key);
      const dayName = DAY_NAMES[dayNumber];
      const hourData = hoursData[key];
      if (hourData === null) {
        converted[dayName] = { is_open: false, open_time: '09:00', close_time: '17:00' };
      } else {
        converted[dayName] = { is_open: true, open_time: hourData.open, close_time: hourData.close };
      }
    });
    return converted;
  };

  const convertNameKeysToNumbers = (hoursData) => {
    if (!hoursData) return {};
    const converted = {};
    Object.keys(hoursData).forEach(dayName => {
      const dayNumber = DAY_NAMES.indexOf(dayName);
      const dayData = hoursData[dayName];
      if (!dayData.is_open) {
        converted[dayNumber] = null;
      } else {
        converted[dayNumber] = { open: dayData.open_time, close: dayData.close_time };
      }
    });
    return converted;
  };

  // Check for hours form changes
  const hasHoursChanges = JSON.stringify(hoursForm) !== JSON.stringify(originalHoursForm);

  // Initialize hours form from settings (only when no pending edits)
  useEffect(() => {
    if (businessHours && Object.keys(businessHours).length > 0) {
      const converted = convertNumberKeysToNames(businessHours);
      if (!hasHoursChanges) {
        setHoursForm(converted);
      }
      setOriginalHoursForm(converted);
    } else {
      // Initialize with default hours if no data
      const defaultHours = {};
      DAY_NAMES.forEach(day => {
        defaultHours[day] = { is_open: false, open_time: '09:00', close_time: '17:00' };
      });
      setHoursForm(defaultHours);
      setOriginalHoursForm(defaultHours);
    }
  }, [businessHours]);

  // Watch saving flag transition: re-snapshot baseline on save completion
  const prevSavingHours = useRef(false);
  useEffect(() => {
    if (prevSavingHours.current && !savingHours) {
      // Save just completed. Re-snapshot from store truth.
      if (businessHours && Object.keys(businessHours).length > 0) {
        const converted = convertNumberKeysToNames(businessHours);
        setOriginalHoursForm(converted);
        setHoursForm(converted);
      }
    }
    prevSavingHours.current = savingHours;
  }, [savingHours, businessHours]);

  // Handle hours form submit - fire and forget
  const handleHoursSubmit = () => {
    const convertedHours = convertNameKeysToNumbers(hoursForm);
    updateBusinessHours(convertedHours);
  };

  // ===== OPERATIONAL SETTINGS SECTION =====
  const initialOperationalForm = {
    min_notice_hours: 0,
    travel_buffer: 0,
    max_concurrent_appointments: 0,
    allow_overlapping_bookings: false,
    tax_enabled: false,
    tax_rate: 0,
    default_payment_terms: 'Due on receipt'
  };
  const [operationalForm, setOperationalForm] = useState(initialOperationalForm);
  const [originalOperationalForm, setOriginalOperationalForm] = useState(initialOperationalForm);

  // Helper to derive operational form data from store
  const deriveOperationalForm = (settings) => ({
    min_notice_hours: settings.min_notice_hours || 0,
    travel_buffer: settings.travel_buffer || 0,
    max_concurrent_appointments: settings.max_concurrent_appointments || 0,
    allow_overlapping_bookings: settings.allow_overlapping_bookings || false,
    tax_enabled: settings.tax_enabled || false,
    tax_rate: settings.tax_rate || 0,
    default_payment_terms: settings.default_payment_terms || 'Due on receipt'
  });

  // Check for operational form changes (mixed types)
  const hasOperationalChanges = Object.keys(operationalForm).some(key => {
    const current = operationalForm[key];
    const original = originalOperationalForm[key];
    if (typeof current === 'boolean') return current !== (original || false);
    if (typeof current === 'number') return current !== (original || 0);
    return current !== (original || '');
  });

  // Initialize operational form from settings (only when no pending edits)
  useEffect(() => {
    if (businessSettings && Object.keys(businessSettings).length > 0) {
      const formData = deriveOperationalForm(businessSettings);
      if (!hasOperationalChanges) {
        setOperationalForm(formData);
      }
      setOriginalOperationalForm(formData);
    }
  }, [businessSettings]);

  // Watch saving flag transition: re-snapshot baseline on save completion
  const prevSavingOperational = useRef(false);
  useEffect(() => {
    if (prevSavingOperational.current && !savingOperationalSettings) {
      // Save just completed. Re-snapshot from store truth.
      if (businessSettings && Object.keys(businessSettings).length > 0) {
        const newBaseline = deriveOperationalForm(businessSettings);
        setOriginalOperationalForm(newBaseline);
        setOperationalForm(newBaseline);
      }
    }
    prevSavingOperational.current = savingOperationalSettings;
  }, [savingOperationalSettings, businessSettings]);

  // Handle operational form submit - diff-only, fire and forget
  const handleOperationalSubmit = () => {
    const changedFields = {};
    Object.keys(operationalForm).forEach(key => {
      const current = operationalForm[key];
      const original = originalOperationalForm[key];
      if (current !== original) {
        changedFields[key] = current;
      }
    });
    if (Object.keys(changedFields).length > 0) {
      updateSettings(changedFields, { savingFlag: 'savingOperationalSettings' });
    }
  };

  // ===== EMAIL CONFIGURATION SECTION =====
  const [emailForm, setEmailForm] = useState({
    reply_to_email: '',
    selectedDomainId: null, // null = platform default, number = custom domain ID
  });
  const [originalEmailForm, setOriginalEmailForm] = useState({});
  const [newDomain, setNewDomain] = useState('');
  const [domainError, setDomainError] = useState('');
  const [showAddDomainForm, setShowAddDomainForm] = useState(false);
  const [expandedDomainId, setExpandedDomainId] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [addingDomain, setAddingDomain] = useState(false);

  // Initialize email form from settings
  useEffect(() => {
    if (emailSettings && Object.keys(emailSettings).length > 0) {
      const activeDomain = getActiveEmailDomain();
      const formData = {
        reply_to_email: emailSettings.reply_to_email || '',
        selectedDomainId: activeDomain?.id || null,
      };
      setEmailForm(formData);
      setOriginalEmailForm(formData);
    }
  }, [emailSettings, getActiveEmailDomain]);

  // Check for email form changes (only reply_to_email and selectedDomainId matter)
  const hasEmailChanges =
    emailForm.reply_to_email !== (originalEmailForm.reply_to_email || '') ||
    emailForm.selectedDomainId !== originalEmailForm.selectedDomainId;

  // Get pending (non-verified) domains
  const getPendingDomains = () => {
    return getEmailDomains().filter(d => !d.is_verified);
  };

  // Validate domain format
  const validateDomain = (domain) => {
    if (!domain) return 'Domain is required';
    // Remove any protocol or www prefix
    const cleaned = domain.replace(/^(https?:\/\/)?(www\.)?/, '').toLowerCase();
    // Basic domain validation: must have at least one dot, no spaces, valid characters
    const domainRegex = /^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}$/;
    if (!domainRegex.test(cleaned)) {
      return 'Please enter a valid domain (e.g., yourdomain.com)';
    }
    // Check if domain already exists
    if (getEmailDomains().some(d => d.domain.toLowerCase() === cleaned)) {
      return 'This domain has already been added';
    }
    return '';
  };

  // Handle adding a new domain for verification
  const handleAddDomain = async () => {
    // Clean domain input
    const cleanedDomain = newDomain.replace(/^(https?:\/\/)?(www\.)?/, '').toLowerCase().trim();

    // Validate
    const error = validateDomain(cleanedDomain);
    if (error) {
      setDomainError(error);
      return;
    }

    setAddingDomain(true);
    setDomainError('');

    try {
      // Default sending email to noreply@domain
      await setupProfessionalDomain(cleanedDomain, `noreply@${cleanedDomain}`);
      setNewDomain('');
      setShowAddDomainForm(false);
      // Expand the newly added domain to show DNS records
      // Note: The domain ID will be set after the socket response updates emailDomains
    } catch (err) {
      setDomainError('Failed to add domain. Please try again.');
    } finally {
      setAddingDomain(false);
    }
  };

  // Handle email settings save (contact email + domain selection)
  const handleEmailSubmit = async () => {
    // Update reply-to email
    await updateEmailSettings({ reply_to_email: emailForm.reply_to_email });

    // Handle domain selection change
    if (emailForm.selectedDomainId !== originalEmailForm.selectedDomainId) {
      if (emailForm.selectedDomainId === null) {
        // Switch to platform default
        await setBasicEmailMode();
      } else {
        // Switch to custom domain
        await setActiveEmailDomain(emailForm.selectedDomainId);
      }
    }

    setOriginalEmailForm(emailForm);
  };

  // Handle copy to clipboard for DNS records
  const handleCopyDNS = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // ===== PAYMENT PROCESSING SECTION =====
  const [setupEmail, setSetupEmail] = useState('');
  const [setupCountry, setSetupCountry] = useState('US');

  // Handle Stripe setup
  const handleStartSetup = (e) => {
    e.preventDefault();
    startExpressSetup(setupEmail, setupCountry);
  };

  // Handle onboarding redirect
  useEffect(() => {
    if (onboardingUrl) {
      setTimeout(() => {
        window.location.href = onboardingUrl;
      }, 2000);
    }
  }, [onboardingUrl]);

  // Handle dashboard redirect
  useEffect(() => {
    if (dashboardUrl) {
      window.open(dashboardUrl, '_blank');
    }
  }, [dashboardUrl]);

  // Navigation sections
  const navSections = [
    { id: 'company', label: 'Company Information' },
    { id: 'hours', label: 'Business Hours' },
    { id: 'operational', label: 'Operational Settings' },
    { id: 'email', label: 'Email Configuration' },
    { id: 'payment', label: 'Payment Processing' },
    { id: 'referrals', label: 'Referral Program' },
    { id: 'integrations', label: 'Integrations' }
  ];

  // Mark as ready after first render
  useEffect(() => {
    setReady(true);
  }, []);

  // Calculate section positions once when ready
  useEffect(() => {
    if (!ready) return;

    const calculatePositions = () => {
      const section = sectionRefs.company?.current;
      if (!section) return;
      const contentDiv = section.closest('.overflow-y-auto');
      if (!contentDiv) return;

      const containerTop = contentDiv.scrollTop;
      const positions = {};

      Object.entries(sectionRefs).forEach(([id, ref]) => {
        if (ref.current) {
          const rect = ref.current.getBoundingClientRect();
          const parentRect = contentDiv.getBoundingClientRect();
          positions[id] = rect.top - parentRect.top + containerTop;
        }
      });

      sectionPositions.current = positions;
    };

    setTimeout(calculatePositions, 100);
  }, [ready]);

  // Scroll spy
  useEffect(() => {
    if (!ready) return;

    const section = sectionRefs.company?.current;
    if (!section) return;
    const contentDiv = section.closest('.overflow-y-auto');
    if (!contentDiv) return;

    const handleScroll = () => {
      const scrollTop = contentDiv.scrollTop;
      const scrollHeight = contentDiv.scrollHeight;
      const clientHeight = contentDiv.clientHeight;
      const positions = sectionPositions.current;

      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;

      if (isAtBottom) {
        setActiveSection(prev => prev !== 'integrations' ? 'integrations' : prev);
        return;
      }

      const sectionOrder = ['integrations', 'referrals', 'payment', 'email', 'operational', 'hours', 'company'];

      for (const sectionId of sectionOrder) {
        const sectionTop = positions[sectionId];
        if (sectionTop !== undefined && scrollTop >= sectionTop - 100) {
          setActiveSection(prev => prev !== sectionId ? sectionId : prev);
          break;
        }
      }
    };

    contentDiv.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      contentDiv.removeEventListener('scroll', handleScroll);
    };
  }, [ready]);

  // Scroll to section
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs[sectionId]?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Loading state
  if (loading && !businessSettings) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading settings...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer heightMode="viewport" maxWidth="full" padding="none">
      <div className="h-full flex flex-col bg-gradient-to-tl from-[#0a0a0a] from-0% via-[#0b0b0f] via-20% via-[#0c0c18] via-40% via-[#0b0b12] via-60% via-[#0b0b0e] via-80% to-[#0a0a0a] to-100%">
        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full pt-12 pl-36 pr-36 flex flex-col">
            {/* Header */}
            <div className="mb-6">
              <div className="space-y-2">
                <h1 className="text-4xl font-bold text-foreground">Business Settings</h1>
                <p className="text-muted-foreground">
                  Configure your business information, operating hours, and preferences
                </p>
              </div>
            </div>

            {/* Side Nav and Content */}
            <div className="flex-1 flex min-h-0">
              {/* Side Navigation */}
              <div className="w-40 flex-shrink-0">
                <div className="space-y-1 sticky top-0">
                  {navSections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={cn(
                        "block w-full text-left text-sm py-1 transition-colors",
                        activeSection === section.id
                          ? "text-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content - scrollable */}
              <div className="flex-1 min-w-0 overflow-y-auto pl-2 space-y-10 pb-12">
                {/* Company Information Section */}
                <div ref={sectionRefs.company} data-section="company" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <Building2 className="w-5 h-5" />
                      Company Information
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Your business details and contact information
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          Company Name <span className="text-red-400">*</span>
                        </Label>
                        <Input
                          type="text"
                          value={companyForm.company_name}
                          onChange={(e) => setCompanyForm({ ...companyForm, company_name: e.target.value })}
                          required
                          className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          Company Phone <span className="text-red-400">*</span>
                        </Label>
                        <Input
                          type="tel"
                          value={companyForm.company_phone}
                          onChange={(e) => setCompanyForm({ ...companyForm, company_phone: e.target.value })}
                          required
                          className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Address</Label>
                      <Input
                        type="text"
                        value={companyForm.company_address}
                        onChange={(e) => setCompanyForm({ ...companyForm, company_address: e.target.value })}
                        className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">City</Label>
                        <Input
                          type="text"
                          value={companyForm.company_city}
                          onChange={(e) => setCompanyForm({ ...companyForm, company_city: e.target.value })}
                          className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">State</Label>
                        <Select
                          value={companyForm.company_state}
                          onValueChange={(value) => setCompanyForm({ ...companyForm, company_state: value })}
                        >
                          <SelectTrigger className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="TX">Texas</SelectItem>
                            <SelectItem value="CA">California</SelectItem>
                            <SelectItem value="NY">New York</SelectItem>
                            <SelectItem value="FL">Florida</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">ZIP Code</Label>
                        <Input
                          type="text"
                          value={companyForm.company_zip}
                          onChange={(e) => setCompanyForm({ ...companyForm, company_zip: e.target.value })}
                          className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Email</Label>
                        <Input
                          type="email"
                          value={companyForm.company_email}
                          onChange={(e) => setCompanyForm({ ...companyForm, company_email: e.target.value })}
                          className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Website</Label>
                        <Input
                          type="url"
                          value={companyForm.company_website}
                          onChange={(e) => setCompanyForm({ ...companyForm, company_website: e.target.value })}
                          className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end pt-2">
                      <Button
                        onClick={handleCompanySubmit}
                        disabled={!hasCompanyChanges || savingCompanyInfo}
                      >
                        {savingCompanyInfo ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Business Hours Section */}
                <div ref={sectionRefs.hours} data-section="hours" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <Clock className="w-5 h-5" />
                      Business Hours
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Operating hours for each day of the week
                    </p>
                  </div>

                  <div className="space-y-3">
                    {DAY_NAMES.map((day) => {
                      const dayData = hoursForm[day] || { is_open: false, open_time: '09:00', close_time: '17:00' };
                      return (
                        <div key={day} className="flex items-center gap-4 p-3 border border-border rounded-lg">
                          <div className="w-32">
                            <Label className="text-sm font-medium capitalize">{day}</Label>
                          </div>

                          <div className="flex items-center gap-2">
                            <Switch
                              checked={dayData.is_open}
                              onCheckedChange={(checked) => {
                                setHoursForm({
                                  ...hoursForm,
                                  [day]: { ...dayData, is_open: checked }
                                });
                              }}
                            />
                            <span className="text-sm text-muted-foreground">
                              {dayData.is_open ? 'Open' : 'Closed'}
                            </span>
                          </div>

                          {dayData.is_open && (
                            <>
                              <div className="flex items-center gap-2">
                                <Label className="text-sm text-muted-foreground">From</Label>
                                <Input
                                  type="time"
                                  value={dayData.open_time}
                                  onChange={(e) => {
                                    setHoursForm({
                                      ...hoursForm,
                                      [day]: { ...dayData, open_time: e.target.value }
                                    });
                                  }}
                                  className="w-32 bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                                />
                              </div>

                              <div className="flex items-center gap-2">
                                <Label className="text-sm text-muted-foreground">To</Label>
                                <Input
                                  type="time"
                                  value={dayData.close_time}
                                  onChange={(e) => {
                                    setHoursForm({
                                      ...hoursForm,
                                      [day]: { ...dayData, close_time: e.target.value }
                                    });
                                  }}
                                  className="w-32 bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                                />
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}

                    <div className="flex justify-end pt-2">
                      <Button
                        onClick={handleHoursSubmit}
                        disabled={!hasHoursChanges || savingHours}
                      >
                        {savingHours ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Operational Settings Section */}
                <div ref={sectionRefs.operational} data-section="operational" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <Settings className="w-5 h-5" />
                      Operational Settings
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Scheduling, tax, and payment preferences
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Scheduling Settings */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-foreground">Scheduling</h4>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Minimum Notice (hours)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={operationalForm.min_notice_hours}
                            onChange={(e) => setOperationalForm({ ...operationalForm, min_notice_hours: parseInt(e.target.value) || 0 })}
                            className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Travel Buffer (minutes)</Label>
                          <Input
                            type="number"
                            min="0"
                            value={operationalForm.travel_buffer}
                            onChange={(e) => setOperationalForm({ ...operationalForm, travel_buffer: parseInt(e.target.value) || 0 })}
                            className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Max Concurrent Appointments</Label>
                        <Input
                          type="number"
                          min="0"
                          value={operationalForm.max_concurrent_appointments}
                          onChange={(e) => setOperationalForm({ ...operationalForm, max_concurrent_appointments: parseInt(e.target.value) || 0 })}
                          className="w-full bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                        />
                      </div>

                      <div className="flex items-center gap-3 p-3 border border-border rounded-lg">
                        <Switch
                          checked={operationalForm.allow_overlapping_bookings}
                          onCheckedChange={(checked) => setOperationalForm({ ...operationalForm, allow_overlapping_bookings: checked })}
                        />
                        <div>
                          <Label className="text-sm font-medium">Allow Overlapping Bookings</Label>
                          <p className="text-xs text-muted-foreground">Permit multiple appointments at the same time</p>
                        </div>
                      </div>
                    </div>

                    {/* Tax Settings */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-foreground">Tax</h4>

                      <div className="flex items-center gap-3 p-3 border border-border rounded-lg">
                        <Switch
                          checked={operationalForm.tax_enabled}
                          onCheckedChange={(checked) => setOperationalForm({ ...operationalForm, tax_enabled: checked })}
                        />
                        <div>
                          <Label className="text-sm font-medium">Enable Tax</Label>
                          <p className="text-xs text-muted-foreground">Apply tax to invoices and services</p>
                        </div>
                      </div>

                      {operationalForm.tax_enabled && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Tax Rate (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={operationalForm.tax_rate}
                            onChange={(e) => setOperationalForm({ ...operationalForm, tax_rate: parseFloat(e.target.value) || 0 })}
                            className="w-full bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                          />
                        </div>
                      )}
                    </div>

                    {/* Payment Terms */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold text-foreground">Payment Terms</h4>

                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Default Payment Terms</Label>
                        <Select
                          value={operationalForm.default_payment_terms}
                          onValueChange={(value) => setOperationalForm({ ...operationalForm, default_payment_terms: value })}
                        >
                          <SelectTrigger className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Due on receipt">Due on receipt</SelectItem>
                            <SelectItem value="Net 15 days">Net 15 days</SelectItem>
                            <SelectItem value="Net 30 days">Net 30 days</SelectItem>
                            <SelectItem value="Net 60 days">Net 60 days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex justify-end pt-2">
                      <Button
                        onClick={handleOperationalSubmit}
                        disabled={!hasOperationalChanges || savingOperationalSettings}
                      >
                        {savingOperationalSettings ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Email Configuration Section */}
                <div ref={sectionRefs.email} data-section="email" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <Mail className="w-5 h-5" />
                      Email Configuration
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Email settings and professional domain
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Contact Email */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Contact Email <span className="text-red-400">*</span>
                      </Label>
                      <Input
                        type="email"
                        value={emailForm.reply_to_email}
                        onChange={(e) => setEmailForm({ ...emailForm, reply_to_email: e.target.value })}
                        placeholder="support@yourcompany.com"
                        required
                        className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                      />
                      <p className="text-xs text-muted-foreground">
                        Where customer replies will be sent
                      </p>
                    </div>

                    {/* Sending Domain Section */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium">Sending Domain</Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          The domain your outbound emails will be sent from
                        </p>
                      </div>

                      {/* Current Status Display */}
                      <div className="p-3 bg-muted/30 rounded-lg border border-border">
                        <div className="flex items-center gap-2 text-sm">
                          <Globe className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Currently sending from:</span>
                          <code className="font-medium text-foreground">
                            {emailForm.selectedDomainId
                              ? getVerifiedEmailDomains().find(d => d.id === emailForm.selectedDomainId)?.sending_email || 'noreply@daybloom.io'
                              : 'noreply@daybloom.io'}
                          </code>
                        </div>
                      </div>

                      {/* Domain Selection - only show if there are verified domains */}
                      {getVerifiedEmailDomains().length > 0 && (
                        <div className="space-y-3">
                          <Label className="text-sm font-medium">Select Sending Domain</Label>
                          <RadioGroup
                            value={emailForm.selectedDomainId?.toString() || 'platform'}
                            onValueChange={(value) => setEmailForm({
                              ...emailForm,
                              selectedDomainId: value === 'platform' ? null : parseInt(value)
                            })}
                            className="space-y-2"
                          >
                            {/* Platform Default Option */}
                            <div className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-muted/20 transition-colors">
                              <RadioGroupItem value="platform" id="domain-platform" />
                              <Label htmlFor="domain-platform" className="flex-1 cursor-pointer">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className="font-medium">noreply@daybloom.io</span>
                                    <p className="text-xs text-muted-foreground">Platform default</p>
                                  </div>
                                  <Badge variant="outline">Default</Badge>
                                </div>
                              </Label>
                            </div>

                            {/* Verified Custom Domains */}
                            {getVerifiedEmailDomains().map((domain) => (
                              <div key={domain.id} className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-muted/20 transition-colors">
                                <RadioGroupItem value={domain.id.toString()} id={`domain-${domain.id}`} />
                                <Label htmlFor={`domain-${domain.id}`} className="flex-1 cursor-pointer">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <span className="font-medium">{domain.sending_email}</span>
                                      <p className="text-xs text-muted-foreground">{domain.domain}</p>
                                    </div>
                                    <Badge variant="success" className="flex items-center gap-1">
                                      <CheckCircle className="w-3 h-3" />
                                      Verified
                                    </Badge>
                                  </div>
                                </Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </div>
                      )}

                      {/* No verified domains message */}
                      {getVerifiedEmailDomains().length === 0 && (
                        <Alert>
                          <Info className="h-4 w-4" />
                          <AlertDescription>
                            Emails are sent from <strong>noreply@daybloom.io</strong>. Verify your own domain below to send from your custom address.
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>

                    {/* Add Custom Domain Section */}
                    <div className="space-y-4 pt-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium">Custom Domain</Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            Add and verify your own domain for sending emails
                          </p>
                        </div>
                        {!showAddDomainForm && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowAddDomainForm(true)}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Domain
                          </Button>
                        )}
                      </div>

                      {/* Add Domain Form */}
                      {showAddDomainForm && (
                        <div className="p-4 border border-border rounded-lg bg-muted/20 space-y-4">
                          <div className="flex items-center justify-between">
                            <h5 className="text-sm font-medium">Add New Domain</h5>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setShowAddDomainForm(false);
                                setNewDomain('');
                                setDomainError('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Domain Name</Label>
                            <div className="flex gap-2">
                              <Input
                                type="text"
                                value={newDomain}
                                onChange={(e) => {
                                  setNewDomain(e.target.value);
                                  setDomainError('');
                                }}
                                placeholder="yourdomain.com"
                                className="bg-zinc-900 border border-zinc-700/50"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddDomain();
                                  }
                                }}
                              />
                              <Button
                                onClick={handleAddDomain}
                                disabled={addingDomain || !newDomain.trim()}
                              >
                                {addingDomain ? (
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  'Verify'
                                )}
                              </Button>
                            </div>
                            {domainError && (
                              <p className="text-xs text-red-400">{domainError}</p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              Enter your domain without http:// or www (e.g., yourdomain.com)
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Pending Domains */}
                      {getPendingDomains().length > 0 && (
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-muted-foreground">Pending Verification</Label>
                          {getPendingDomains().map((domain) => (
                            <div key={domain.id} className="border border-border rounded-lg overflow-hidden">
                              {/* Domain Header */}
                              <div
                                className="p-3 bg-muted/20 flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors"
                                onClick={() => setExpandedDomainId(expandedDomainId === domain.id ? null : domain.id)}
                              >
                                <div className="flex items-center gap-3">
                                  <Globe className="w-4 h-4 text-muted-foreground" />
                                  <div>
                                    <span className="font-medium text-sm">{domain.domain}</span>
                                    <p className="text-xs text-muted-foreground">noreply@{domain.domain}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className="flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    Pending
                                  </Badge>
                                  {expandedDomainId === domain.id ? (
                                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                  )}
                                </div>
                              </div>

                              {/* Expanded DNS Records */}
                              {expandedDomainId === domain.id && (
                                <div className="p-4 border-t border-border space-y-4">
                                  <Alert>
                                    <AlertCircle className="w-4 h-4" />
                                    <AlertDescription className="text-xs">
                                      Add these DNS records to your domain provider. Verification may take up to 48 hours.
                                    </AlertDescription>
                                  </Alert>

                                  {/* DNS Records */}
                                  <div className="space-y-2">
                                    {Object.entries(domain.dns_records || {}).map(([recordType, recordData]) => {
                                      if (!recordData?.host) return null;
                                      const displayType = recordType.includes('dkim') ? 'DKIM' : recordType.includes('spf') ? 'TXT (SPF)' : recordType.includes('mx') ? 'MX' : 'CNAME';
                                      const fieldId = `${domain.id}-${recordType}-host`;
                                      const valueId = `${domain.id}-${recordType}-value`;

                                      return (
                                        <div key={recordType} className="border border-border rounded-lg p-3 bg-background">
                                          <Badge variant="outline" className="mb-2">{displayType}</Badge>
                                          <div className="space-y-2">
                                            <div>
                                              <div className="flex items-center justify-between mb-1">
                                                <Label className="text-xs font-medium text-muted-foreground">Host/Name</Label>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => handleCopyDNS(recordData.host, fieldId)}
                                                  className="h-6 px-2"
                                                >
                                                  {copiedField === fieldId ? (
                                                    <><Check className="w-3 h-3 mr-1" /><span className="text-xs">Copied</span></>
                                                  ) : (
                                                    <><Copy className="w-3 h-3 mr-1" /><span className="text-xs">Copy</span></>
                                                  )}
                                                </Button>
                                              </div>
                                              <code className="block text-xs bg-muted p-2 rounded break-all">{recordData.host}</code>
                                            </div>
                                            <div>
                                              <div className="flex items-center justify-between mb-1">
                                                <Label className="text-xs font-medium text-muted-foreground">Value</Label>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => handleCopyDNS(recordData.value, valueId)}
                                                  className="h-6 px-2"
                                                >
                                                  {copiedField === valueId ? (
                                                    <><Check className="w-3 h-3 mr-1" /><span className="text-xs">Copied</span></>
                                                  ) : (
                                                    <><Copy className="w-3 h-3 mr-1" /><span className="text-xs">Copy</span></>
                                                  )}
                                                </Button>
                                              </div>
                                              <code className="block text-xs bg-muted p-2 rounded break-all">{recordData.value}</code>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {/* Check Verification Button */}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => verifyDomain(domain.id)}
                                    disabled={checkingDomainVerification}
                                  >
                                    {checkingDomainVerification ? (
                                      <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                        Checking...
                                      </>
                                    ) : (
                                      'Check Verification Status'
                                    )}
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end pt-2">
                      <Button
                        onClick={handleEmailSubmit}
                        disabled={!hasEmailChanges || savingEmailSettings}
                      >
                        {savingEmailSettings ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Changes
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Payment Processing Section */}
                <div ref={sectionRefs.payment} data-section="payment" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <CreditCard className="w-5 h-5" />
                      Payment Processing
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Connect Stripe to accept payments from customers
                    </p>
                  </div>

                  <div className="space-y-4">
                    {status === 'disconnected' && (
                      <div className="p-6 border border-border rounded-lg bg-muted/20">
                        <h4 className="text-sm font-semibold text-foreground mb-4">Connect Stripe Account</h4>
                        <p className="text-sm text-muted-foreground mb-6">
                          Link your Stripe account to accept credit card payments directly through your invoices.
                        </p>

                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Email</Label>
                            <Input
                              type="email"
                              value={setupEmail}
                              onChange={(e) => setSetupEmail(e.target.value)}
                              placeholder="your@email.com"
                              className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Country</Label>
                            <Select value={setupCountry} onValueChange={setSetupCountry}>
                              <SelectTrigger className="bg-zinc-900 border border-zinc-700/50 shadow-[0_1px_2px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.05)] transition-all duration-200 focus-visible:border-zinc-500 focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.2),0_4px_12px_rgba(255,255,255,0.1),0_2px_4px_rgba(0,0,0,0.4)] focus-visible:-translate-y-0.5">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="US">United States</SelectItem>
                                <SelectItem value="CA">Canada</SelectItem>
                                <SelectItem value="GB">United Kingdom</SelectItem>
                                <SelectItem value="AU">Australia</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <Button onClick={handleStartSetup} disabled={!setupEmail || setupInProgress}>
                            {setupInProgress ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                Connecting...
                              </>
                            ) : (
                              'Connect Stripe'
                            )}
                          </Button>
                        </div>
                      </div>
                    )}

                    {(status === 'pending_onboarding' || status === 'restricted' || status === 'charges_only') && (
                      <div className="p-6 border border-border rounded-lg bg-muted/20">
                        <div className="flex items-start gap-3 mb-4">
                          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="text-sm font-semibold text-foreground">Setup Incomplete</h4>
                            <p className="text-sm text-muted-foreground mt-1">{statusMessage || nextAction}</p>
                          </div>
                        </div>

                        <Button onClick={createOnboardingLink} disabled={setupInProgress}>
                          {setupInProgress ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                              Loading...
                            </>
                          ) : (
                            'Continue Setup'
                          )}
                        </Button>
                      </div>
                    )}

                    {status === 'active' && (
                      <div className="space-y-4">
                        <Alert>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <AlertDescription className="text-sm">
                            Your Stripe account is connected and ready to accept payments.
                            {canAcceptPayments && ' You can now enable online payments for your invoices.'}
                          </AlertDescription>
                        </Alert>

                        <div className="p-6 border border-border rounded-lg bg-muted/20 space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-sm font-semibold text-foreground">Stripe Dashboard</h4>
                              <p className="text-sm text-muted-foreground mt-1">
                                View transactions, payouts, and account settings
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              onClick={getExpressDashboardLink}
                              disabled={refreshingStatus}
                            >
                              Open Dashboard
                            </Button>
                          </div>

                          <Separator />

                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-sm font-semibold text-foreground">Disconnect Account</h4>
                              <p className="text-sm text-muted-foreground mt-1">
                                Remove Stripe integration from your account
                              </p>
                            </div>
                            <Button
                              variant="destructive"
                              onClick={disconnectStripeAccount}
                              disabled={disconnecting}
                            >
                              {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {stripeLoading && !stripeConnection && (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Referral Program Section */}
                <div ref={sectionRefs.referrals} data-section="referrals" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <Gift className="w-5 h-5" />
                      Referral Program
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Reward customers for referring new clients to your business
                    </p>
                  </div>

                  <ReferralSettings />
                </div>

                <Separator />

                <Separator />

                {/* Google Business Profile Integration Section */}
                <div ref={sectionRefs.integrations} data-section="integrations" className="scroll-mt-6 space-y-5">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2 text-foreground">
                      <Zap className="w-5 h-5" />
                      Integrations
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      Connect with third-party services to enhance your workflow
                    </p>
                  </div>

                  {/* Google Business Profile Integration */}
                  <div>
                    <h4 className="text-md font-semibold flex items-center gap-2 text-foreground mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      Google Business Profile
                    </h4>
                    <GBPSettings />
                  </div>

                  {/* Microsoft Integration (Unified: Calendar + Teams) */}
                  <div className="mt-8">
                    <h4 className="text-md font-semibold flex items-center gap-2 text-foreground mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0">
                        <path d="M11.4 2H2v9.4h9.4V2z" fill="#F25022"/>
                        <path d="M22 2h-9.4v9.4H22V2z" fill="#7FBA00"/>
                        <path d="M11.4 12.6H2V22h9.4v-9.4z" fill="#00A4EF"/>
                        <path d="M22 12.6h-9.4V22H22v-9.4z" fill="#FFB900"/>
                      </svg>
                      Microsoft Integration
                    </h4>
                    <MicrosoftIntegrationSettings />
                  </div>

                  {/* Zoom Integration */}
                  <div className="mt-8">
                    <h4 className="text-md font-semibold flex items-center gap-2 text-foreground mb-4">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0">
                        <rect width="24" height="24" rx="4" fill="#2D8CFF"/>
                        <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h7A1.5 1.5 0 0 1 14 8.5v7A1.5 1.5 0 0 1 12.5 17h-7A1.5 1.5 0 0 1 4 15.5v-7zm10 2.1 4.5-2.8a.5.5 0 0 1 .75.43v6.54a.5.5 0 0 1-.75.43L14 12.4v-1.8z" fill="white"/>
                      </svg>
                      Zoom Integration
                    </h4>
                    <ZoomIntegrationSettings />
                  </div>

                  {/* Meeting Provider Preference - Only shows when both Teams and Zoom are connected */}
                  <div className="mt-8">
                    <MeetingProviderPreference />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default BusinessSettings;
